import { MarinaService } from './../../services/marina.service';
import { Token } from './../../models/token';
import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Marina } from 'src/app/models';
import { MarinaCompany } from 'src/app/models/marina-company';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { InvoiceService } from 'src/app/services/invoice.service';
import { BoatService } from 'src/app/services/boat.service';
import { StorageUtil } from 'src/app/utils/storage.util';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit {
  private static readonly WHATSAPP_SUPPORT_PHONE = '5548991250397';
  private static readonly WHATSAPP_SUPPORT_MESSAGE =
    'Olá! Preciso de ajuda com o sistema Easymarine.';
  private static readonly SUPPORT_PORTAL_URL = 'https://portal.easymarine.com.br';

  checked = true;
  user: Token;
  marinas: any[] = [];
  selectedMarina: Marina;
  newSelectedMarina: { id; name };
  company: MarinaCompany;
  language: any;
  finances = false;
  days: number;
  mobile = false;

  showFranquiaBoats = false;
  qtdMaxBoat = 0;
  qtdActiveBoats = 0;
  franquiaPercent = 0;

  @HostListener('window:resize', ['$event']) onScroll($event: Event): void {
    this.adjustWidth();
  }

  constructor(
    private route: ActivatedRoute,
    private invoiceService: InvoiceService,
    private translate: TranslateService,
    public router: Router,
    private marinaService: MarinaService,
    private boatService: BoatService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.adjustWidth();
    // await this.findMarinas();
    this.user = StorageUtil.getUser();
    this.selectedMarina = StorageUtil.getMarina();
    if(this.selectedMarina) {
      this.newSelectedMarina = this.marinas.find( marina => marina.id === this.selectedMarina.id);
    }
    const currentParams = Object.assign({}, this.route.snapshot.queryParams);
    if (currentParams && currentParams.company) {
      this.company = this.selectedMarina.marinaCompanies.find(
        (m) => m.id === currentParams.company
      );
    }
    this.language = this.translate.currentLang;
    this.loadFranquiaBoats();
    this.countInvoicesOfBillsNotSent();
    BroadcastService.get('showHideSidenav').subscribe((checked) => {
      this.checked = checked;
    });
    BroadcastService.get('companyChanged').subscribe(
      (companyChanged: { company: number; finances: boolean }) => {
        this.company = null;
        this.finances = companyChanged.finances;
        if (companyChanged.company) {
          this.company = this.selectedMarina.marinaCompanies.find(
            (m) => m.id === companyChanged.company
          );
        }
      }
    );
  }


  findMarinas(): Promise<void> {
    return new Promise(resolve => {
      this.marinaService
      .getMarinas()
      .subscribe((marinaList) => {
        marinaList.forEach((marina) => this.marinas.push(marina));
        resolve();
      },
      error => {
        console.log(error)
      });
    })
  }

  showHide(): void {
    this.checked = !this.checked;
    // Botão único é a fonte da verdade: persiste a preferência por usuário (EAS-249).
    StorageUtil.setSidenavOpened(this.checked);
    BroadcastService.get('showHideSidenav').emit(this.checked);
  }

  getMarinaName(): string {
    return this.finances
      ? this.company
        ? this.company.companyFederalName
        : StorageUtil.getMarina().companyName
      : StorageUtil.getMarinaName();
  }

  getLoggedUser(): string {
    return StorageUtil.getUserName();
  }

  getLogoMarina(): string {
    return StorageUtil.getLogoMarina();
  }

  private loadFranquiaBoats(): void {
    const marina = StorageUtil.getMarina();
    if (!marina || !marina.controlQtdBoats || !marina.qtdMaxBoat) {
      this.showFranquiaBoats = false;
      return;
    }
    this.qtdMaxBoat = marina.qtdMaxBoat;
    this.boatService.countActive().subscribe({
      next: (count) => {
        this.qtdActiveBoats = count || 0;
        this.franquiaPercent = this.qtdMaxBoat > 0
          ? (this.qtdActiveBoats / this.qtdMaxBoat) * 100
          : 0;
        // Só exibe o alerta no header a partir de 90% da franquia consumida.
        this.showFranquiaBoats = this.franquiaPercent >= 90;
      },
      error: (err) => {
        console.log(err);
        this.showFranquiaBoats = false;
      },
    });
  }

  get franquiaExcedentes(): number {
    return Math.max(0, this.qtdActiveBoats - this.qtdMaxBoat);
  }

  get isFranquiaWarning(): boolean {
    return this.franquiaPercent >= 90 && this.franquiaPercent < 100;
  }

  get isFranquiaAtLimit(): boolean {
    return this.franquiaPercent >= 100 && this.franquiaExcedentes === 0;
  }

  get isFranquiaExceeded(): boolean {
    return this.franquiaExcedentes > 0;
  }

  /**
   * EAS-298 (CA-04): o texto secundário de cada estado saiu do header e virou
   * tooltip. Copy reaproveitado do que já era exibido, sem texto novo.
   */
  get franquiaTooltip(): string {
    if (this.isFranquiaWarning) {
      return `${this.qtdActiveBoats} de ${this.qtdMaxBoat} embarcações usadas. Você está próximo do limite.`;
    }
    if (this.isFranquiaAtLimit) {
      return `Limite de ${this.qtdMaxBoat} embarcações atingido. Faça upgrade do seu plano.`;
    }
    return `${this.qtdActiveBoats} de ${this.qtdMaxBoat} embarcações — ${this.franquiaExcedentes} excedentes. `
      + 'Faça upgrade do seu plano.';
  }

  openWhatsappSupport(): void {
    const url = `https://wa.me/${HeaderComponent.WHATSAPP_SUPPORT_PHONE}`
      + `?text=${encodeURIComponent(HeaderComponent.WHATSAPP_SUPPORT_MESSAGE)}`;
    window.open(url, '_blank');
  }

  openSupportPortal(): void {
    window.open(HeaderComponent.SUPPORT_PORTAL_URL, '_blank');
  }

  countInvoicesOfBillsNotSent(): void {
    this.invoiceService.countInvoicesOfBillsNotSent().subscribe(
      (data) => {
        if (data > 0) {
          Swal.fire({
            title: 'Atenção',
            text: (data > 1 ? 'Existem ' : 'Existe ')
              .concat(data.toString())
              .concat(
                data > 1
                  ? ' boletos que não foram enviados e-mail'
                  : ' boleto que não foi enviado e-mail'
              ),
            icon: 'warning',
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'Fechar',
          }).then((result) => {
            BroadcastService.get('messageDismissed').emit();
          });
        } else {
          setTimeout(() => {
            BroadcastService.get('messageDismissed').emit();
          }, 1000);
        }
      },
      () => null
    );
  }

  async selectMarina(): Promise<void> {
    await this.findSelectedMarina();
    this.router.navigate(['/app/dashboard']);
    BroadcastService.get('changeMarina').emit();
  }

  async findSelectedMarina(): Promise<void>{
    return new Promise(async resolve => {
      this.marinaService
      .getMarina(this.newSelectedMarina.id)
      .subscribe((marina) => {
        this.selectedMarina = marina;
        StorageUtil.setMarina(this.selectedMarina);
        resolve();
      });
    });
  }

  adjustWidth(): void {
    this.mobile = window.outerWidth <= 992;
    // No mobile a barra inicia recolhida (responsivo); no desktop respeita a
    // preferência salva do usuário, com padrão aberto no primeiro acesso (EAS-249).
    this.checked = this.mobile ? false : (StorageUtil.getSidenavOpened() ?? true);
    BroadcastService.get('showHideSidenav').emit(this.checked);
  }
}
